import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Portfolio, PortfolioSummary, PortfolioTotals } from '@/types/portfolio'

/**
 * Hook for fetching portfolio data.
 *
 * Uses React Query for caching and automatic refetching.
 */

// Query keys for cache management
export const portfolioKeys = {
  all: ['portfolio'] as const,
  personal: () => [...portfolioKeys.all, 'personal'] as const,
  company: (id: string) => [...portfolioKeys.all, 'company', id] as const,
  summary: () => [...portfolioKeys.all, 'summary'] as const,
}

/**
 * Hook to fetch the current user's portfolio.
 */
export function usePortfolio() {
  return useQuery({
    queryKey: portfolioKeys.personal(),
    queryFn: () => api.get<Portfolio>('/api/v2/my/portfolio'),
    staleTime: 30000, // Consider data fresh for 30 seconds
  })
}

/**
 * Hook to fetch portfolio summary (for quick loading).
 */
export function usePortfolioSummary() {
  return useQuery({
    queryKey: portfolioKeys.summary(),
    queryFn: () => api.get<PortfolioSummary>('/api/v2/my/portfolio/summary'),
    staleTime: 30000,
  })
}

/**
 * Calculate portfolio totals from positions.
 */
export function calculateTotals(portfolio: Portfolio): PortfolioTotals {
  let bookValue = 0
  let stocksVolume = 0
  let bondsVolume = 0
  let coinsVolume = 0
  let reposVolume = 0

  for (const pos of portfolio.positions) {
    bookValue += pos.volume

    switch (pos.type) {
      case 'STOCK':
        stocksVolume += pos.volume
        break
      case 'BOND':
      case 'INTEREST_TENDER_BOND':
        bondsVolume += pos.volume
        break
      case 'COIN':
        coinsVolume += pos.volume
        break
      case 'REPO':
      case 'SYSTEM_REPO':
        reposVolume += pos.volume
        break
    }
  }

  return {
    cash: portfolio.cash,
    uncommittedCash: portfolio.cash - portfolio.committedCash,
    bookValue,
    totalValue: portfolio.cash + bookValue,
    stocksVolume,
    bondsVolume,
    coinsVolume,
    reposVolume,
  }
}
